import React, { useEffect, useMemo, useState } from "react"
import { Pressable, ScrollView, View } from "react-native"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import { TimePickerModal } from "react-native-paper-dates"
import { connect } from "react-redux"
import moment from "moment"

import FilledButtonM from "@common/components/button/FilledButtonM"
import MobileDatePickerModal from "@common/components/modal/MobileDatePickerModal"
import TextM from "@common/components/text/TextM"
import { theme } from "@common/theme/theme"

import { SectionHeader } from "./SectionHeader"
import { DATETIME_FORMAT, sectionStyle, stickyFooterStyle } from "./utils"

/**
 * Individual flow: parent picks one exact (date + time) per requested
 * hour. The submit payload sends `requested_dates: ["YYYY-MM-DD HH:mm"]`
 * and the backend short-circuits auto-routing — ops takes it from there,
 * which is why no slot-grid is needed.
 *
 * The parent can pick any date up to 30 days out — the deadline section
 * is hidden for individual format (the picked dates implicitly define the
 * timeline), so no shared deadline window applies here.
 */
const IndividualDatesStepBase = ({
    formValues,
    setFormValues,
    goToNextStep,
    user,
    serverTimezone,
}) => {
    const hours = formValues?.hours ?? 1
    const requestedDates = formValues?.requested_dates ?? []

    const isTzDifferentThanServer =
        !!user?.timezone && !!serverTimezone && user.timezone !== serverTimezone

    const { minDate, maxDate } = useMemo(() => {
        // Mirrors the shared format's 3-day floor: ops needs at least 3
        // days of lead time to confirm a tutor for an individual session.
        const min = moment().add(3, "days").startOf("day")
        const max = moment().add(30, "days").endOf("day")
        return { minDate: min, maxDate: max }
    }, [])

    // Keep `requested_dates` aligned with `hours` (the parent can flip
    // back to step 1 and bump hours up/down). Functional updater so the
    // effect doesn't have to depend on the parent's `formValues`
    // reference — that reference churns on every keystroke and would
    // re-run this effect needlessly.
    useEffect(() => {
        setFormValues((prev) => {
            const current = prev?.requested_dates ?? []
            if (current.length === hours) return prev
            const next = [...current]
            while (next.length < hours) next.push(null)
            while (next.length > hours) next.pop()
            return { ...(prev ?? {}), requested_dates: next }
        })
    }, [hours, setFormValues])

    const [datePickerIndex, setDatePickerIndex] = useState(null)
    const [timePickerIndex, setTimePickerIndex] = useState(null)

    const setSlot = (index, value) => {
        const next = [...(formValues?.requested_dates ?? [])]
        while (next.length < hours) next.push(null)
        next[index] = value
        setFormValues({ ...(formValues ?? {}), requested_dates: next })
    }

    const slotMoment = (value) =>
        value && moment(value, DATETIME_FORMAT, true).isValid()
            ? moment(value, DATETIME_FORMAT)
            : null

    const allFilled =
        requestedDates.length === hours &&
        requestedDates.every((v) => {
            const m = slotMoment(v)
            return m && m.isSameOrAfter(minDate)
        })

    const currentDateValue = (i) =>
        slotMoment(requestedDates[i])?.format("YYYY-MM-DD") ?? null
    const currentTimeValue = (i) => {
        const m = slotMoment(requestedDates[i])
        if (!m) return { hours: 18, minutes: 0 }
        return { hours: m.hours(), minutes: m.minutes() }
    }

    const pillStyle = (filled) => ({
        flex: 1,
        flexDirection: "row",
        alignItems: "center",
        paddingVertical: 10,
        paddingHorizontal: 12,
        borderWidth: 1.5,
        borderColor: filled ? theme.colors.primary : theme.colors.lightMore,
        backgroundColor: filled
            ? theme.colors.material.primary["50"]
            : theme.colors.white,
        borderRadius: 10,
    })

    return (
        <View style={{ flex: 1, width: "100%" }}>
            <ScrollView
                style={{ width: "100%", flex: 1 }}
                contentContainerStyle={{
                    paddingHorizontal: 20,
                    paddingBottom: 12,
                }}
                keyboardShouldPersistTaps="handled"
                nestedScrollEnabled
            >
                <View style={sectionStyle}>
                    <SectionHeader
                        icon="calendar-day"
                        label="Dates et heures souhaitées"
                    />
                    <TextM
                        fontSize={11}
                        color={theme.colors.grey}
                        wrap
                        style={{ marginBottom: 12, marginTop: -2 }}
                    >
                        Choisissez la date et l&apos;heure exactes de chaque
                        cours
                        {isTzDifferentThanServer ? (
                            <>
                                , exprimées en{" "}
                                <TextM
                                    fontSize={11}
                                    fontWeight="Bold"
                                    color={theme.colors.grey}
                                >
                                    heure de Paris
                                </TextM>
                            </>
                        ) : null}
                        . Sélectionnez un créneau entre le{" "}
                        {minDate.format("DD/MM/YYYY")} et le{" "}
                        {maxDate.format("DD/MM/YYYY")}.
                    </TextM>

                    {Array.from({ length: hours }).map((_, i) => {
                        const value = requestedDates[i]
                        const m = slotMoment(value)
                        const dateLabel = m ? m.format("ddd DD/MM") : "Date"
                        const timeLabel = m ? m.format("HH[h]mm") : "Heure"
                        const filledDate = !!m
                        const filledTime = !!m
                        return (
                            <View
                                key={i}
                                style={{
                                    flexDirection: "row",
                                    alignItems: "center",
                                    marginBottom: 10,
                                }}
                            >
                                <View style={{ width: 26 }}>
                                    <TextM
                                        fontWeight="Bold"
                                        fontSize={14}
                                        color={theme.colors.primary}
                                    >
                                        {i + 1}.
                                    </TextM>
                                </View>
                                <Pressable
                                    onPress={() => setDatePickerIndex(i)}
                                    style={[
                                        pillStyle(filledDate),
                                        { marginRight: 8 },
                                    ]}
                                >
                                    <FontAwesome5
                                        name="calendar"
                                        size={12}
                                        color={theme.colors.primary}
                                        style={{ marginRight: 8 }}
                                        solid
                                    />
                                    <TextM
                                        fontSize={13}
                                        fontWeight={
                                            filledDate ? "Bold" : "Medium"
                                        }
                                        color={
                                            filledDate
                                                ? theme.colors.primary
                                                : theme.colors.dark
                                        }
                                    >
                                        {dateLabel}
                                    </TextM>
                                </Pressable>
                                <Pressable
                                    onPress={() => setTimePickerIndex(i)}
                                    style={pillStyle(filledTime)}
                                >
                                    <FontAwesome5
                                        name="clock"
                                        size={12}
                                        color={theme.colors.primary}
                                        style={{ marginRight: 8 }}
                                        solid
                                    />
                                    <TextM
                                        fontSize={13}
                                        fontWeight={
                                            filledTime ? "Bold" : "Medium"
                                        }
                                        color={
                                            filledTime
                                                ? theme.colors.primary
                                                : theme.colors.dark
                                        }
                                    >
                                        {timeLabel}
                                    </TextM>
                                </Pressable>
                            </View>
                        )
                    })}
                </View>
            </ScrollView>

            <View style={stickyFooterStyle}>
                <FilledButtonM
                    label="Continuer"
                    onPress={() => goToNextStep()}
                    disabled={!allFilled}
                    style={{ width: "100%" }}
                />
            </View>

            <MobileDatePickerModal
                visible={datePickerIndex !== null}
                onDismiss={() => setDatePickerIndex(null)}
                value={
                    datePickerIndex !== null
                        ? currentDateValue(datePickerIndex)
                        : null
                }
                validRange={{
                    startDate: minDate.toDate(),
                    endDate: maxDate.toDate(),
                }}
                onConfirm={(dd) => {
                    const i = datePickerIndex
                    setDatePickerIndex(null)
                    if (i === null) return
                    const m = slotMoment(requestedDates[i])
                    const timePart = m ? m.format("HH:mm") : "18:00"
                    setSlot(i, `${dd} ${timePart}`)
                    // Chain straight into the time picker: a date pick
                    // without a confirmed time always leaves the slot at
                    // the placeholder 18:00, which is rarely what the
                    // parent meant. Opening the time picker right after
                    // keeps the two-step pick feeling like one gesture.
                    setTimePickerIndex(i)
                }}
                label="Date du cours"
            />

            <TimePickerModal
                visible={timePickerIndex !== null}
                onDismiss={() => setTimePickerIndex(null)}
                hours={
                    timePickerIndex !== null
                        ? currentTimeValue(timePickerIndex).hours
                        : 18
                }
                minutes={
                    timePickerIndex !== null
                        ? currentTimeValue(timePickerIndex).minutes
                        : 0
                }
                label="Heure du cours"
                cancelLabel="Annuler"
                confirmLabel="Confirmer"
                keyboardIcon="keyboard"
                clockIcon="clock"
                locale="fr"
                onConfirm={({ hours: h, minutes: mm }) => {
                    const i = timePickerIndex
                    setTimePickerIndex(null)
                    if (i === null) return
                    const existing = slotMoment(requestedDates[i])
                    const datePart = existing
                        ? existing.format("YYYY-MM-DD")
                        : minDate.format("YYYY-MM-DD")
                    const hh = String(h).padStart(2, "0")
                    const mins = String(mm).padStart(2, "0")
                    setSlot(i, `${datePart} ${hh}:${mins}`)
                }}
            />
        </View>
    )
}

const mapStateToProps = (state) => ({
    serverTimezone: state.data.staticData?.server_timezone,
})

export const IndividualDatesStep = connect(mapStateToProps)(
    IndividualDatesStepBase
)
